import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"

import type { PublishArtifactDraft } from "../utils/agent-artifacts-utils"
import type { HostedAgentSession, HostedAgentSummary } from "@/api/client/hosted-agents"
import type { ChatAppResponse } from "@/api/models"

import { MAX_FILES_UPLOAD } from "@/app/pages/modules/ai-explorer-constants"
import { AgentRunningBanner } from "@/features/ai-explorer/components/AgentRunningBanner"
import { useBaseSettings } from "@/features/base-settings/hooks/use-base-settings"
import { Chat } from "@/features/chat/components/chat"
import { ExplorerLoadingState } from "@/features/chat/components/explorer-loading-state"
import { ToolApprovalOverlay } from "@/features/chat/components/tool-approval-overlay"
import { durableTurnAcceptErrorText } from "@/features/chat/utils/durable-turn-error-i18n"
import { useAIExplorerStore } from "@/stores/ai-explorer-store"
import { useContentSidebarStore } from "@/stores/content-sidebar-store"
import { useHostedAgentArtifactSeedStore } from "@/stores/hosted-agent-artifact-seed-store"
import { translateActivityMessage } from "@/utils/activity-message-translator"
import { getResponseContent, getResponseContext } from "@/utils/response-format-utils"

import { AgentFailureRecovery } from "./agent-failure-recovery"
import { AgentGreeting } from "./agent-greeting"
import { AgentPresence } from "./agent-presence"
import { AgentSourcesPanel } from "./agent-sources-panel"
import { AgentUserMessage } from "./agent-user-message"
import { ArtifactCandidateCard } from "./artifact-candidate-card"
import { PublishArtifactDialog } from "./publish-artifact-dialog"
import { useAgentChat } from "../hooks/use-agent-chat"
import { useAgentComposer } from "../hooks/use-agent-composer"
import { useAgentImproveAction } from "../hooks/use-agent-improve-action"
import { useAgentThreadTitle } from "../hooks/use-agent-thread-title"
import { useHostedAgentArtifacts } from "../hooks/use-hosted-agent-artifacts"
import { buildPublishDraft } from "../utils/agent-artifacts-utils"
import {
  agentChatPresence,
  type AgentPresence as AgentPresenceState,
  latestAnswerActivity,
  sessionErrorAlreadyInThread,
  threadFailureRecoveryActions,
  workingActivityKindFromMessage,
} from "../utils/agent-presence-utils"
import {
  AGENT_ANSWER_COLUMN_CLASS,
  AGENT_CHAT_DROPZONE_OPTIONS,
  AGENT_COMPOSER_CONTROL_CLASS,
  agentTurnDurationMs,
  agentWorkspaceLayoutClass,
} from "../utils/agent-workspace-utils"

type AgentChatViewProps = {
  agent: HostedAgentSummary
  session?: HostedAgentSession
  /**
   * Called with the thread an attachment upload had to create from the
   * greeting state, so the owning module can put it in the URL before the
   * turn runs. Without it the module's session sync closes that thread and
   * the files end up on a session the turn never uses.
   */
  onSessionEnsured?: (sessionId: string) => boolean | void | Promise<boolean | void>
  /** Live presence of the open thread, for the thread list's row of that thread. */
  onPresenceChange?: (presence: AgentPresenceState) => void
}

/**
 * Hosted-agent chat surface (HERMES Fas 4). Pure reuse of the chat-turns
 * stack: `Chat.Content`/`Chat.UserMessage`/`Chat.SystemMessage` render the
 * conversation (tool activity included, via SystemMessage's activity
 * history), `Chat.Footer` is the composer, `ToolApprovalOverlay` handles
 * in-turn tool approvals and `AgentRunningBanner` surfaces a background run
 * on an opened-but-empty session. Orchestration (streaming, durable turns,
 * hydration, session binding) lives in `useAgentChat`.
 *
 * Task attachments are parsed by the backend before a durable turn starts.
 * Existing indexed attachments and generated assistant_files keep their shared
 * chat rendering. New source metadata/content is inspected separately.
 *
 * Still deliberately lean: no voice, no model/reasoning pickers — a hosted
 * agent's model and tools are resolved server-side from its definition.
 */
export function AgentChatView({
  agent,
  session,
  onSessionEnsured,
  onPresenceChange,
}: AgentChatViewProps) {
  const { t } = useTranslation()
  const chat = useAgentChat(agent)
  const {
    aborted,
    chatMessageStreamEnd,
    chatState,
    changeTokenCounts,
    currentSessionAgentRun,
    currentSessionId,
    defaultSearchServiceId,
    files,
    filesInIndex,
    handleCancelRequest,
    handleContinue,
    handleResetFiles,
    handleSendQuestion,
    history,
    isCurrentSessionAgentRunActive,
    moduleName,
    loadingMessage,
    removingFileName,
    sessionFileRefs,
    sessionType,
    totalTokenCount,
  } = chat

  const { gptModel } = useBaseSettings({ sessionType })
  // useGPTModelSettings can return null before configuration is hydrated,
  // despite the legacy indexed-array type declaring a model unconditionally.
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  const maxTokens = gptModel?.input_token_limit ?? 0
  const isContentSidebarOpen = useContentSidebarStore((state) => state.isOpen)
  // Same store instance `useAgentChat` writes to — response timing lives there.
  const explorerStore = useAIExplorerStore(sessionType)

  const answers = useMemo(
    () =>
      chatState.streamingAnswer
        ? [...chatState.messages, chatState.streamingAnswer]
        : chatState.messages,
    [chatState.messages, chatState.streamingAnswer]
  )

  const currentSession = useMemo(
    () =>
      session ??
      (history.sessions.find((entry) => entry != null && entry.id === currentSessionId) as
        | HostedAgentSession
        | undefined),
    [session, history.sessions, currentSessionId]
  )
  // Unknown ownership is not write permission. It briefly occurs during cold hydration.
  const isReader = Boolean(currentSessionId) && currentSession?.currentUserRole !== "owner"
  const approvalRef = useRef<HTMLDivElement>(null)

  // --- B4: artifact seed + composer pre-fill -------------------------------
  const pendingSeed = useHostedAgentArtifactSeedStore((state) => state.pendingSeed)
  const sessionSeeds = useHostedAgentArtifactSeedStore((state) => state.sessionSeeds)
  const bindPendingSeed = useHostedAgentArtifactSeedStore((state) => state.bindPendingSeed)
  const setSessionSeed = useHostedAgentArtifactSeedStore((state) => state.setSessionSeed)

  // A seed set by "Continue working" binds to the NEXT session the composer
  // creates: only on a null → id transition, so opening an existing thread
  // (or the old session still being mounted during navigation) never claims it.
  const previousSessionIdRef = useRef<string | null>(currentSessionId ?? null)
  useEffect(() => {
    const previous = previousSessionIdRef.current
    previousSessionIdRef.current = currentSessionId ?? null
    if (previous === null && currentSessionId && pendingSeed) bindPendingSeed(currentSessionId)
  }, [currentSessionId, pendingSeed, bindPendingSeed])
  const activeSeed =
    (currentSessionId ? sessionSeeds[currentSessionId] : undefined) ??
    (!currentSessionId ? pendingSeed : undefined) ??
    null
  const seedForAgent = activeSeed?.agentSlug === agent.slug ? activeSeed : null

  const {
    workspaceRef,
    permanentSources,
    isUploading,
    insertDraft,
    handleSetQuestion,
    handleAddSourceFiles,
    handleRemoveComposerFile,
    handleComposerSend,
    handleRejectedSourceFiles,
    sourceDropzoneOptions,
  } = useAgentComposer({ agent, chat, isReader, onSessionEnsured })

  // --- B4: publish dialog + candidate cards --------------------------------
  const [publishDraft, setPublishDraft] = useState<PublishArtifactDraft | null>(null)
  const [isPublishOpen, setPublishOpen] = useState(false)
  const [dismissedCandidates, setDismissedCandidates] = useState<ReadonlySet<string>>(
    () => new Set()
  )
  // The candidate card that opened the current publish dialog: dismissed on a
  // successful publish so the same turn is never offered twice.
  const publishingCandidateKeyRef = useRef<string | null>(null)

  // `artifact_candidate` is persisted in the message context, so hydrated
  // threads would re-offer every historical candidate. The library is the
  // durable record: a message that already produced an artifact is done.
  // (Shares the rail's query — one request per agent.)
  const artifactsQuery = useHostedAgentArtifacts(isReader ? undefined : agent.slug)
  const publishedMessageIds = useMemo(() => {
    const ids = new Set<string>()
    for (const artifact of artifactsQuery.data ?? []) {
      if (artifact.provenance.message_id) ids.add(artifact.provenance.message_id)
    }
    return ids
  }, [artifactsQuery.data])

  const answersRef = useRef(answers)
  answersRef.current = answers

  // The draft publishes the reply's artifact, never its prose + XML (see buildPublishDraft).
  const openPublishDialog = useCallback(
    (response: ChatAppResponse, index: number, candidateKey?: string) => {
      const draft = buildPublishDraft(response, {
        fromCandidate: candidateKey !== undefined,
        index,
        sessionId: currentSessionId,
        tableTitle: t("agents.artifacts.table_title"),
        // The library artifact this reply continues travels on the draft (vN+1).
        seed: seedForAgent,
        userMessages: answersRef.current.slice(0, index + 1).map((answer) => answer[0]),
        library: artifactsQuery.data,
        agentSlug: agent.slug,
      })
      if (!draft) return
      publishingCandidateKeyRef.current = candidateKey ?? null
      setPublishDraft(draft)
      setPublishOpen(true)
    },
    [currentSessionId, seedForAgent, artifactsQuery.data, agent.slug, t]
  )

  // SystemMessage is memoised: hand each message a stable handler keyed on its
  // render key and resolve the response (and the current session/seed) at click time.
  const openPublishDialogRef = useRef(openPublishDialog)
  openPublishDialogRef.current = openPublishDialog
  const publishHandlersRef = useRef(new Map<string, () => void>())
  const getPublishHandler = useCallback((key: string, index: number) => {
    const existing = publishHandlersRef.current.get(key)
    if (existing) return existing
    const handler = () => {
      const answer = answersRef.current.at(index)
      if (answer) openPublishDialogRef.current(answer[1], index)
    }
    publishHandlersRef.current.set(key, handler)
    return handler
  }, [])

  // The in-thread failure card has no handlers. These are the same Retry /
  // Continue actions the suppressed session card would have shown — labels
  // included — and they stay off when that card is already on screen.
  const failureRecovery = threadFailureRecoveryActions(chatState)

  const showEmptyState =
    answers.length === 0 &&
    !chatState.lastQuestion[0] &&
    !chatState.isLoading &&
    !chatState.isHydrating &&
    !chatState.streamingAnswer &&
    !chatState.error &&
    !aborted &&
    !isCurrentSessionAgentRunActive

  const isBusy =
    chatState.isLoading ||
    chatState.isStreaming ||
    chatState.isAwaitingSave ||
    chatState.cancelPending ||
    Boolean(chatState.durableTurn) ||
    isCurrentSessionAgentRunActive

  const getImproveAction = useAgentImproveAction({
    answersRef,
    disabled: isBusy,
    insertDraft,
  })
  const presence = agentChatPresence({
    state: chatState,
    sessionId: currentSessionId ?? null,
    aborted: Boolean(aborted),
    run: currentSessionAgentRun,
  })
  useEffect(() => {
    onPresenceChange?.(presence)
  }, [presence, onPresenceChange])
  // Live verb (local Hermes: live_status = verb + argument preview).
  // Heartbeats keep this honest on long tool calls.
  const latestActivity = latestAnswerActivity(chatState.streamingAnswer, chatState.messages)
  const liveStatus = latestActivity ? translateActivityMessage(latestActivity, t) : ""
  const liveActivityKind = workingActivityKindFromMessage(latestActivity)
  const pendingApproval =
    chatState.toolApprovalSessionId === currentSessionId &&
    chatState.toolApprovals.some((approval) => approval.status === "pending")
  // "Response complete · 3.6 s": the same timer AI Explorer uses (this chat
  // runs on `useAIExplorerChat`, so the store row is already filled in).
  const turnDurationMs = explorerStore((state) => agentTurnDurationMs(state.responseTiming))

  // The rail must show the new thread's title straight away (#3165).
  const firstTurnText = chatState.messages[0]?.[0] || chatState.lastQuestion[0]
  useAgentThreadTitle(agent.slug, currentSessionId, firstTurnText)
  const sourcesPanel = (
    <AgentSourcesPanel
      agentRef={agent.slug}
      busy={isBusy || isUploading}
      canEdit={!isReader}
      sessionId={currentSessionId ?? null}
      triggerClassName={isReader ? undefined : AGENT_COMPOSER_CONTROL_CLASS}
    />
  )
  return (
    <div ref={workspaceRef} className={agentWorkspaceLayoutClass(showEmptyState)}>
      {!showEmptyState && (
        <AgentPresence
          activity={liveStatus || loadingMessage}
          activityKind={liveActivityKind}
          agent={agent}
          attention={pendingApproval}
          durationMs={turnDurationMs}
          presence={presence}
          onReview={!isReader ? () => approvalRef.current?.focus() : undefined}
          onInspectError={() =>
            workspaceRef.current?.querySelector<HTMLElement>("[data-agent-error]")?.focus()
          }
          onStop={
            !isReader &&
            (chatState.isLoading ||
              chatState.isStreaming ||
              chatState.cancelPending ||
              Boolean(chatState.durableTurn))
              ? handleCancelRequest
              : undefined
          }
        />
      )}
      <Chat.Content>
        {showEmptyState && (
          <AgentGreeting agent={agent} disabled={isReader || isUploading} onChoose={insertDraft} />
        )}

        {/* Background run on an opened session whose answer hasn't been
            persisted yet (AgentRunningBanner pattern). */}
        {isCurrentSessionAgentRunActive &&
          currentSessionAgentRun &&
          !chatState.streamingAnswer &&
          answers.length === 0 && (
            <AgentRunningBanner agentRun={currentSessionAgentRun} sessionType={sessionType} />
          )}

        {answers.map((answer: [string, ChatAppResponse], index: number) => {
          const response = answer[1]
          const messageId = response.messageId ?? getResponseContext(response).message_id
          const key = messageId ?? `${currentSessionId ?? "session"}-answer-${index}`
          const isStreamingAnswer =
            Boolean(chatState.streamingAnswer) && index === answers.length - 1
          const candidate = getResponseContext(response).artifact_candidate
          const showCandidate =
            !!candidate &&
            !isReader &&
            !!response.completed &&
            !isStreamingAnswer &&
            !dismissedCandidates.has(key) &&
            !(messageId && publishedMessageIds.has(messageId))

          // The failure card lives on the message itself (`sessionErrorAlreadyInThread`), so
          // "Visa problemet" focuses this section once the duplicate session-level card is gone.
          const carriesThreadError =
            index === answers.length - 1 && "error" in response && Boolean(response.error)

          return (
            <section
              key={key}
              className={AGENT_ANSWER_COLUMN_CLASS}
              data-agent-error={carriesThreadError || undefined}
              tabIndex={carriesThreadError ? -1 : undefined}
            >
              {answer[0].trim() ? <AgentUserMessage message={answer[0]} /> : null}
              <Chat.SystemMessage
                inlineArtifactPreviews
                answer={response}
                citationSection={defaultSearchServiceId}
                index={index}
                isLastMessage={index === answers.length - 1}
                isReader={isReader}
                isStreaming={isStreamingAnswer}
                messageId={messageId ?? undefined}
                moduleName={moduleName}
                noCitation={false}
                sessionId={currentSessionId ?? undefined}
                sessionType={sessionType}
                showFollowupQuestions={false}
                showPublishArtifact={!isReader}
                userQuery={answer[0]}
                onPublishArtifactClicked={getPublishHandler(key, index)}
              >
                {!isReader &&
                response.completed &&
                !response.interrupted &&
                !response.error &&
                !isStreamingAnswer &&
                getResponseContent(response).trim()
                  ? getImproveAction(key, index)
                  : null}
              </Chat.SystemMessage>
              {showCandidate ? (
                <ArtifactCandidateCard
                  agentName={agent.profile.name}
                  candidate={candidate}
                  onAccept={() => openPublishDialog(response, index, key)}
                  onDismiss={() => setDismissedCandidates((previous) => new Set(previous).add(key))}
                />
              ) : null}
              <AgentFailureRecovery
                actions={carriesThreadError ? failureRecovery : null}
                onContinue={handleContinue}
                onRetry={() =>
                  void handleSendQuestion({ question: chatState.lastQuestion[0], files: [] })
                }
              />
            </section>
          )
        })}

        {chatState.isLoading && !chatState.streamingAnswer && (
          <ExplorerLoadingState
            lastQuestion={chatState.lastQuestion[0]}
            sessionId={currentSessionId ?? undefined}
          />
        )}

        {chatState.error && !sessionErrorAlreadyInThread(chatState) ? (
          <div
            data-agent-error
            className="focus-visible:ring-ring focus-visible:ring-2"
            tabIndex={-1}
          >
            {!chatState.canContinue && chatState.lastQuestion[0] && (
              <AgentUserMessage message={chatState.lastQuestion[0]} />
            )}
            <Chat.Error
              canContinue={chatState.canContinue}
              // An accept refusal (agent deleted, switched off, access lost)
              // is a raw ApiError here; its code says it in the reader's language.
              error={
                durableTurnAcceptErrorText(chatState.error) ?? (chatState.error as Error).toString()
              }
              onContinue={handleContinue}
              onRetry={
                chatState.lastQuestion[0]
                  ? // Same empty file list as `handleComposerSend`: a retry
                    // must not pick up composer attachments and fall off the
                    // durable transport (they upload separately, before the
                    // turn).
                    () =>
                      void handleSendQuestion({ question: chatState.lastQuestion[0], files: [] })
                  : undefined
              }
            />
          </div>
        ) : null}

        {aborted ? (
          <>
            {!chatState.canContinue && aborted[0] && <AgentUserMessage message={aborted[0]} />}
            <Chat.Error
              canContinue={chatState.canContinue}
              error={t("pages.common.request_aborted") + (aborted[1] ? `\n\n${aborted[1]}` : "")}
              onContinue={handleContinue}
            />
          </>
        ) : null}

        <div ref={chatMessageStreamEnd} />
      </Chat.Content>

      <div ref={approvalRef} aria-label={t("agents.presence_actions.review")} tabIndex={-1}>
        <ToolApprovalOverlay
          activeSessionId={currentSessionId ?? null}
          durableTurn={chatState.durableTurn}
          sidebarExpanded={isContentSidebarOpen}
          toolApprovalSessionId={chatState.toolApprovalSessionId}
          approvals={
            !isReader && chatState.toolApprovalSessionId === currentSessionId
              ? chatState.toolApprovals
              : []
          }
        />
      </div>

      {/* A reader gets no composer, so the sources control cannot live in its
          options row — inspecting what the agent used stays available. */}
      {isReader ? (
        <div className="mx-auto flex w-full max-w-3xl justify-end px-4 pb-1">{sourcesPanel}</div>
      ) : null}
      {/* Nothing sits between the thread and the composer any more (#3165):
          the audience pill moved into the header, and the sources hint became
          the "+" button's tooltip. "Sources · N" is now a control inside the
          composer's own options row, next to "+". */}
      <Chat.Footer
        clearOnSend
        focusOnMount
        hideVoiceMode
        // The attach ("+") button lives in the options row, and it is the only
        // visible way to add a file here — drag-drop and paste are
        // undiscoverable. Left to `isTextareaFocused` it would disappear after
        // every sent turn, so it is pinned open exactly as in AI Explorer.
        // Nothing else surfaces in that row: voice, reasoning and model pickers
        // are all off for agents.
        showChatOptions
        attachTitle={t("agents.sources.composer_hint")}
        availableFiles={files}
        cancelAvailable={false}
        changeTokenCounts={changeTokenCounts}
        defaultFilePickerSource="local"
        disableFileSelection={!permanentSources.isSuccess || isUploading}
        disableSend={isReader || isBusy || isUploading}
        dropzoneOptions={sourceDropzoneOptions}
        // The "+" opens the shared "Select Files" dialog, which on its own
        // accepts images (Explorer does). Give it the agent's source types so
        // an image is refused when picked, with the same message a drop gets,
        // and its help text lists only what the agent can read.
        filePickerAccept={AGENT_CHAT_DROPZONE_OPTIONS.accept}
        filePickerInfoMessage={t("agents.sources.formats")}
        filesInIndex={filesInIndex}
        isReader={isReader}
        isUploading={isUploading}
        maxFiles={MAX_FILES_UPLOAD}
        maxTokens={maxTokens}
        placeholder={t("agents.chat.placeholder", { name: agent.profile.name })}
        removingFileName={removingFileName}
        resetFiles={handleResetFiles}
        sessionFileRefs={sessionFileRefs}
        sessionType={sessionType}
        showCancelOption={isBusy}
        totalTokenCount={totalTokenCount}
        onCancel={handleCancelRequest}
        onFilePickerFilesRejected={handleRejectedSourceFiles}
        onFilesAdded={handleAddSourceFiles}
        onRemoveFile={handleRemoveComposerFile}
        onSend={handleComposerSend}
        onSetQuestion={handleSetQuestion}
      >
        {/* `children` is the composer options row's slot, right after "+". */}
        {isReader ? null : sourcesPanel}
      </Chat.Footer>

      {/* `Chat.Footer` already carries the AI disclaimer under the composer,
          which is the only thing the mockup keeps there. While a file is being
          parsed the live status joins it — nothing else. */}
      {isUploading && !isReader ? (
        <p className="text-muted-foreground px-4 pb-2 text-center text-xs" role="status">
          {t("agents.sources.processing_upload")}
        </p>
      ) : null}

      <PublishArtifactDialog
        agentName={agent.profile.name}
        agentSlug={agent.slug}
        draft={publishDraft}
        open={isPublishOpen}
        onOpenChange={setPublishOpen}
        onPublished={(artifact) => {
          const candidateKey = publishingCandidateKeyRef.current
          publishingCandidateKeyRef.current = null
          if (candidateKey) {
            setDismissedCandidates((previous) => new Set(previous).add(candidateKey))
          }
          // A publish from a seeded thread advances the seed so the next
          // publish in the same thread proposes vN+2, not a sibling of vN+1.
          if (currentSessionId && artifact.parent_id) {
            setSessionSeed(currentSessionId, {
              agentSlug: agent.slug,
              artifactId: artifact.id,
              rootId: artifact.root_id,
              title: artifact.title,
              version: artifact.version,
            })
          }
        }}
      />
    </div>
  )
}
